import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, CheckCircle, Users, Baby, DollarSign, Monitor, Mail, MapPin, Heart, Brain, Clock, FileText, Shield, ArrowRight } from 'lucide-react';
import SEO from '../components/SEO';
import { FadeInUp } from '../components/Animations';

const ASDAssessmentPage = () => {
  const [openFaq, setOpenFaq] = useState(null);

  const targetGroups = [
    {
      icon: <Users className="w-8 h-8" />,
      title: 'ASD Assessments for Women',
      description: 'Many women reach adulthood without a diagnosis because autism often presents differently in females. Our assessments are designed to recognise masking, camouflaging, and the social-communication differences that are commonly missed in women and girls.'
    },
    {
      icon: <Users className="w-8 h-8" />,
      title: 'ASD Assessments for Men',
      description: 'Whether you have been questioning your experiences for years or were recently referred, our comprehensive assessments for men and boys use evidence-based diagnostic tools to provide clarity and direction.'
    },
    {
      icon: <Baby className="w-8 h-8" />,
      title: 'ASD Assessments for Children',
      description: 'Early identification can make a significant difference. Our child assessments are developmentally appropriate and conducted in close collaboration with parents, caregivers, and educators to build a complete picture.'
    },
    {
      icon: <Heart className="w-8 h-8" />,
      title: 'Adult Autism Assessments',
      description: 'Many adults seek an assessment after recognising traits in themselves or a family member. We specialise in adult autism diagnosis, understanding the unique challenges of identifying autism later in life.'
    },
    {
      icon: <Monitor className="w-8 h-8" />,
      title: 'Telehealth Assessments Australia-Wide',
      description: 'Our assessments are fully conducted via secure telehealth video sessions, making a professional ASD assessment accessible from anywhere in Australia — no travel required.'
    },
    {
      icon: <DollarSign className="w-8 h-8" />,
      title: 'Medicare Rebates & Flexible Payment Plans',
      description: 'Medicare rebates and flexible payment plans are available, and we support clients with NDIS funding, to help make assessments more accessible. Contact us to discuss your funding situation and what rebates may apply to you.'
    }
  ];

  const process = [
    { step: '1', title: 'Initial Enquiry', description: 'Contact us through our online form or phone to discuss your needs. We will help you understand whether an ASD assessment is the right step and answer any questions about the process.' },
    { step: '2', title: 'Pre-Assessment Questionnaires', description: 'Before your first session, you will complete a set of background questionnaires covering your developmental history, current experiences, and daily functioning. For children, parents and teachers may also complete forms.' },
    { step: '3', title: 'Clinical Interview', description: 'An in-depth telehealth interview with our psychologist covering your developmental history, social communication, sensory experiences, routines, and how these impact your daily life.' },
    { step: '4', title: 'Standardised Assessments', description: 'We use gold-standard diagnostic tools including the MIGDAS-2 and other recognised instruments. Collateral information from family members or partners is also gathered when appropriate.' },
    { step: '5', title: 'Comprehensive Report', description: 'You receive a detailed written report with diagnostic conclusions, assessment results, strengths identified, and personalised recommendations for support, accommodations, and next steps.' },
    { step: '6', title: 'Feedback Session', description: 'A dedicated session to walk through your results, discuss what the findings mean for you, and answer any questions. We take a collaborative, neurodiversity-affirming approach to feedback.' }
  ];

  const faqs = [
    {
      question: 'What is an ASD assessment and who is it for?',
      answer: 'An ASD (Autism Spectrum Disorder) assessment is a comprehensive evaluation conducted by a registered psychologist to determine whether an individual meets the diagnostic criteria for autism. Our assessments are available for adults, adolescents, and children of all genders. Whether you are a woman who suspects she has been masking, a man exploring a late diagnosis, or a parent seeking answers for your child — our assessments are tailored to your specific situation.'
    },
    {
      question: 'How long does an ASD assessment take?',
      answer: 'A full ASD assessment typically involves 4 to 6 hours of clinical time, spread across multiple telehealth sessions over several weeks. This includes completing pre-assessment questionnaires, a detailed clinical interview, standardised assessments, collateral interviews where appropriate, and a feedback session. Spreading the assessment across sessions ensures thoroughness without fatigue.'
    },
    {
      question: 'Can autism assessments really be done via telehealth?',
      answer: 'Yes. Telehealth autism assessments are supported by current research and follow the same rigorous clinical standards as in-person assessments. Our psychologist uses validated assessment tools that are approved for telehealth delivery. Telehealth also offers benefits including reduced sensory overwhelm from unfamiliar clinical settings, the comfort of being assessed in your own environment, and access for Australians in regional and rural areas.'
    },
    {
      question: 'Do you assess adults for autism?',
      answer: 'Absolutely. A growing number of adults are seeking autism assessments, often after recognising traits in themselves, a child, or a family member. Many adults — particularly women — were missed as children because their presentation did not match outdated stereotypes. We have specific expertise in adult autism diagnosis and understanding how autism presents across the lifespan.'
    },
    {
      question: 'How is autism different in women and girls?',
      answer: 'Autism in women and girls often looks different from the traditional male presentation that most diagnostic criteria were based on. Women are more likely to develop sophisticated masking or camouflaging strategies, mimic social behaviours, internalise their difficulties (presenting as anxiety or depression rather than behavioural challenges), and maintain surface-level social relationships that hide underlying struggles. Our assessments specifically account for these differences.'
    },
    {
      question: 'Do you assess children for autism?',
      answer: 'Yes, we assess children of all ages. For younger children, the assessment process involves close collaboration with parents, caregivers, and often teachers or childcare providers. We gather information from multiple sources to build a comprehensive understanding of your child across different environments. Our approach is child-centred, supportive, and designed to be as comfortable as possible.'
    },
    {
      question: 'Do I need a GP referral for an ASD assessment?',
      answer: 'A GP referral is not required to book an ASD assessment with us. However, if you wish to access Medicare rebates, you will need a Mental Health Care Plan (MHCP) from your GP, which provides a set number of rebated sessions. We can guide you through this process. NDIS participants can also use their funding — contact us to discuss your plan.'
    },
    {
      question: 'Are Medicare rebates or payment plans available?',
      answer: 'Yes. We support clients with NDIS funding and Medicare rebates. If you have a Mental Health Care Plan from your GP, you may be eligible for Medicare rebates on sessions. NDIS participants can use their plan funding for assessments. Flexible payment plans are also available to help spread the cost of your assessment. Contact us to discuss your specific funding situation and we will help you understand your options.'
    },
    {
      question: 'Will I receive a formal diagnosis and written report?',
      answer: 'Yes. If diagnostic criteria are met, you will receive a formal ASD diagnosis supported by a comprehensive written report. The report includes a summary of assessment findings, diagnostic conclusions, identified strengths, areas of challenge, and personalised recommendations for support, workplace accommodations, educational adjustments, or therapeutic pathways. This report can be used for NDIS applications, workplace disclosure, educational support, or personal understanding.'
    },
    {
      question: 'What happens after I receive my diagnosis?',
      answer: 'After your assessment, we provide a feedback session to discuss your results in detail and answer any questions. Depending on your needs, we can help connect you with appropriate supports including therapy (we offer ongoing telehealth therapy), NDIS plan development support, workplace or educational accommodations guidance, and community resources. A diagnosis is the beginning of a journey toward better self-understanding, not an endpoint.'
    },
    {
      question: 'Can you assess for both ASD and ADHD together?',
      answer: 'Yes. Many individuals have co-occurring ASD and ADHD, and we offer combined assessments that evaluate both conditions. This is especially important because the symptoms of ASD and ADHD can overlap and interact in complex ways. A combined assessment ensures that nothing is missed and that recommendations address the full picture.'
    },
    {
      question: 'What areas of Australia do you cover?',
      answer: 'Because our assessments are conducted entirely via telehealth, we can see clients anywhere in Australia. We regularly work with clients in Sydney, Melbourne, Brisbane, Perth, Adelaide, Hobart, Darwin, Canberra, the Gold Coast, and regional and remote areas across all states and territories. All you need is a stable internet connection and a private space for your sessions.'
    },
    {
      question: 'What is a neurodiversity-affirming approach?',
      answer: 'A neurodiversity-affirming approach views autism as a natural variation in how the brain works, rather than a deficit or disorder to be fixed. This means we focus on understanding your unique strengths and challenges, supporting your wellbeing and self-understanding, and providing practical strategies — rather than trying to make you appear neurotypical. Our goal is to help you understand yourself better and access the right support.'
    }
  ];

  return (
    <>
      <SEO
        title="Autism (ASD) Assessment Online Australia | Adults, Children, Women & Men"
        description="Comprehensive telehealth autism assessments for adults, children, women, and men across Australia. Neurodiversity-affirming ASD assessments with Medicare rebates and flexible payment plans available. Professional diagnosis and detailed reports."
        keywords="autism assessment Australia, ASD assessment online, autism diagnosis telehealth, adult autism assessment, autism assessment women, autism assessment children, NDIS autism assessment, Medicare autism assessment, autism assessment Brisbane, autism assessment Melbourne, autism assessment Sydney, neurodiversity affirming assessment, telehealth autism assessment Australia"
        url="/asd-assessment"
      />

      {/* Hero Section */}
      <section className="hero-gradient py-16 md:py-20 relative overflow-hidden">
        <div className="container mx-auto px-4">
          <FadeInUp>
            <div className="max-w-4xl mx-auto text-center">
              <h1 className="text-4xl md:text-5xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
                <span style={{ color: 'var(--blue-primary)' }}>Autism (ASD) Assessments</span> Online Across Australia
              </h1>
              <p className="text-lg md:text-xl mb-4" style={{ color: 'var(--text-secondary)' }}>
                Comprehensive autism spectrum assessments for adults, children, women, and men — fully conducted via secure telehealth from anywhere in Australia.
              </p>
              <p className="text-base mb-8" style={{ color: 'var(--text-muted)' }}>
                Medicare rebates and flexible payment plans available. Neurodiversity-affirming approach.
              </p>
              <Link
                to="/contact"
                data-testid="hero-cta-button"
                className="px-8 py-4 bg-[var(--blue-primary)] text-white rounded-full font-medium hover:opacity-90 transition-all duration-300 hover:scale-105 inline-block"
              >
                Enquire About an Assessment
              </Link>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* Who Is This Assessment For - AI Optimised Section */}
      <section className="section">
        <div className="container mx-auto px-4">
          <FadeInUp>
            <div className="max-w-4xl mx-auto mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
                Who Is an ASD Assessment For?
              </h2>
              <p className="text-lg mb-4" style={{ color: 'var(--text-secondary)' }}>
                An autism assessment may be right for you or your child if you have noticed differences in social communication, sensory sensitivities, a preference for routines, or difficulty navigating everyday situations that others seem to manage easily. Many of our clients come to us after years of feeling different without understanding why.
              </p>
              <p className="text-lg mb-6" style={{ color: 'var(--text-secondary)' }}>
                Our assessments are suitable for:
              </p>
              <ul className="space-y-3 text-lg mb-6" style={{ color: 'var(--text-secondary)' }}>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 mr-3 mt-1 flex-shrink-0" style={{ color: 'var(--blue-primary)' }} />
                  <span><strong>Adults</strong> who suspect they may be autistic, including those who were never assessed as children or received a different diagnosis such as anxiety or depression</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 mr-3 mt-1 flex-shrink-0" style={{ color: 'var(--blue-primary)' }} />
                  <span><strong>Women and girls</strong> whose autism may have been missed due to masking, camouflaging, or presentations that differ from traditional male-focused criteria</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 mr-3 mt-1 flex-shrink-0" style={{ color: 'var(--blue-primary)' }} />
                  <span><strong>Men and boys</strong> seeking a comprehensive, evidence-based assessment conducted by an experienced psychologist</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 mr-3 mt-1 flex-shrink-0" style={{ color: 'var(--blue-primary)' }} />
                  <span><strong>Children and adolescents</strong> whose parents, teachers, or paediatricians have identified developmental or social-communication concerns</span>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-5 h-5 mr-3 mt-1 flex-shrink-0" style={{ color: 'var(--blue-primary)' }} />
                  <span><strong>Individuals seeking a combined ASD and ADHD assessment</strong>, as these conditions frequently co-occur and benefit from being assessed together</span>
                </li>
              </ul>
              <p className="text-base" style={{ color: 'var(--text-muted)' }}>
                No GP referral is required to book. However, a Mental Health Care Plan from your GP is needed to access Medicare rebates.
              </p>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* Assessment Types Cards */}
      <section className="section gray-bg">
        <div className="container mx-auto px-4">
          <FadeInUp>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
                Specialised Autism Assessments for Every Individual
              </h2>
              <p className="text-lg max-w-3xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
                Autism presents differently across genders, ages, and individuals. Our assessments are tailored to recognise these unique presentations and ensure nothing is missed.
              </p>
            </div>
          </FadeInUp>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {targetGroups.map((group, index) => (
              <FadeInUp key={group.title} delay={index * 0.1}>
                <div className="bg-white p-6 rounded-2xl shadow-md hover:shadow-lg transition-shadow">
                  <div className="inline-flex items-center justify-center w-14 h-14 bg-[var(--blue-pale)] rounded-full mb-4" style={{ color: 'var(--navy-primary)' }}>
                    {group.icon}
                  </div>
                  <h3 className="text-xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>
                    {group.title}
                  </h3>
                  <p style={{ color: 'var(--text-secondary)' }}>
                    {group.description}
                  </p>
                </div>
              </FadeInUp>
            ))}
          </div>
        </div>
      </section>

      {/* How Does a Telehealth Assessment Work */}
      <section className="section">
        <div className="container mx-auto px-4">
          <FadeInUp>
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
                How Does a Telehealth Autism Assessment Work?
              </h2>
              <p className="text-lg mb-4" style={{ color: 'var(--text-secondary)' }}>
                Our ASD assessments are conducted entirely via secure video telehealth sessions, following the same rigorous clinical standards as in-person assessments. Current research supports the validity and reliability of telehealth autism assessments when conducted by experienced professionals using validated tools.
              </p>
              <p className="text-lg mb-6" style={{ color: 'var(--text-secondary)' }}>
                Telehealth assessments offer several benefits: you can complete your assessment from the comfort and familiarity of your own home, there is reduced sensory overwhelm compared to unfamiliar clinical settings, and it removes travel barriers — making professional autism assessments accessible to Australians in every state and territory, including regional and remote areas.
              </p>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* Process Section */}
      <section className="section gray-bg">
        <div className="container mx-auto px-4">
          <FadeInUp>
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>
                What Does the ASD Assessment Process Involve?
              </h2>
              <p className="text-lg max-w-3xl mx-auto" style={{ color: 'var(--text-secondary)' }}>
                Our comprehensive six-step process is designed to be thorough, supportive, and conducted at a pace that works for you.
              </p>
            </div>
          </FadeInUp>

          <div className="max-w-4xl mx-auto">
            <div className="space-y-6">
              {process.map((item, index) => (
                <FadeInUp key={item.step} delay={index * 0.1}>
                  <div className="bg-white p-6 rounded-xl shadow-md flex items-start space-x-4">
                    <div className="flex-shrink-0 w-12 h-12 bg-[var(--blue-primary)] text-white rounded-full flex items-center justify-center text-xl font-bold">
                      {item.step}
                    </div>
                    <div className="flex-grow">
                      <h3 className="text-xl font-bold mb-2" style={{ color: 'var(--text-primary)' }}>
                        {item.title}
                      </h3>
                      <p style={{ color: 'var(--text-secondary)' }}>
                        {item.description}
                      </p>
                    </div>
                  </div>
                </FadeInUp>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What to Expect / Assessment Includes Section */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center max-w-6xl mx-auto">
            <FadeInUp>
              <div>
                <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
                  What Is Included in Your ASD Assessment?
                </h2>
                <p className="text-lg mb-4" style={{ color: 'var(--text-secondary)' }}>
                  Our assessments use gold-standard diagnostic tools and are conducted by an experienced registered psychologist. We take a neurodiversity-affirming approach, recognising autism as a natural variation in how the brain works, not a deficit.
                </p>
                <div className="bg-[var(--gray-100)] p-6 rounded-xl">
                  <h3 className="text-xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Every assessment includes:</h3>
                  <ul className="space-y-2">
                    {[
                      'Comprehensive developmental and life history review',
                      'Validated autism assessment tools (including MIGDAS-2)',
                      'Clinical observation and structured interview',
                      'Collateral information from family, partners, or teachers where appropriate',
                      'Sensory profile and daily functioning evaluation',
                      'Detailed written diagnostic report with personalised recommendations',
                      'Dedicated feedback session to discuss your results',
                      'Guidance on next steps including therapy, NDIS, workplace accommodations, or educational support'
                    ].map((item) => (
                      <li key={item} className="flex items-start">
                        <CheckCircle className="w-5 h-5 mr-3 flex-shrink-0 mt-0.5" style={{ color: 'var(--blue-primary)' }} />
                        <span style={{ color: 'var(--text-secondary)' }}>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </FadeInUp>
            <FadeInUp delay={0.2}>
              <div className="relative">
                <img 
                  src="/images/asd-puzzle.png" 
                  alt="Autism spectrum assessment illustration showing the comprehensive and supportive nature of our ASD evaluation process"
                  className="rounded-2xl shadow-xl"
                />
              </div>
            </FadeInUp>
          </div>
        </div>
      </section>

      {/* What Happens After Your Diagnosis */}
      <section className="section gray-bg">
        <div className="container mx-auto px-4">
          <FadeInUp>
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
                What Happens After Your ASD Assessment?
              </h2>
              <p className="text-lg mb-6" style={{ color: 'var(--text-secondary)' }}>
                Receiving a diagnosis is the beginning of a journey toward better self-understanding, not an endpoint. After your assessment, we support you with:
              </p>
              <div className="grid md:grid-cols-2 gap-6">
                {[
                  { icon: <FileText className="w-6 h-6" />, title: 'Comprehensive Report', description: 'A detailed written report you can share with your GP, employer, school, or NDIS planner as needed.' },
                  { icon: <Heart className="w-6 h-6" />, title: 'Ongoing Therapy', description: 'We offer continued telehealth therapy to support your wellbeing, self-understanding, and daily functioning after diagnosis.' },
                  { icon: <Shield className="w-6 h-6" />, title: 'NDIS & Funding Support', description: 'Your report can be used for NDIS applications. We can help guide you through the process of accessing funding and supports.' },
                  { icon: <Brain className="w-6 h-6" />, title: 'Personalised Strategies', description: 'Practical recommendations tailored to your life — whether that means workplace accommodations, educational adjustments, or daily coping strategies.' },
                ].map((item) => (
                  <div key={item.title} className="bg-white p-6 rounded-xl shadow-md">
                    <div className="inline-flex items-center justify-center w-12 h-12 bg-[var(--blue-pale)] rounded-full mb-4" style={{ color: 'var(--navy-primary)' }}>
                      {item.icon}
                    </div>
                    <h3 className="text-lg font-bold mb-2" style={{ color: 'var(--text-primary)' }}>{item.title}</h3>
                    <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{item.description}</p>
                  </div>
                ))}
              </div>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* Australia-Wide Coverage */}
      <section className="section">
        <div className="container mx-auto px-4">
          <FadeInUp>
            <div className="max-w-4xl mx-auto text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[var(--blue-pale)] rounded-full mb-6">
                <MapPin className="w-8 h-8" style={{ color: 'var(--blue-primary)' }} />
              </div>
              <h2 className="text-3xl md:text-4xl font-bold mb-6" style={{ color: 'var(--text-primary)' }}>
                Autism Assessments Available Across All of Australia
              </h2>
              <p className="text-lg mb-4" style={{ color: 'var(--text-secondary)' }}>
                Because our assessments are conducted entirely via telehealth, we work with clients in every state and territory. You do not need to travel to a clinic or live near a specialist — all you need is a stable internet connection and a quiet, private space.
              </p>
              <p className="text-base mb-8" style={{ color: 'var(--text-muted)' }}>
                We regularly see clients in Sydney, Melbourne, Brisbane, Perth, Adelaide, Gold Coast, Hobart, Darwin, Canberra, and regional areas across New South Wales, Victoria, Queensland, Western Australia, South Australia, Tasmania, Northern Territory, and the ACT.
              </p>
              <div className="flex flex-wrap justify-center gap-3">
                {['Sydney', 'Melbourne', 'Brisbane', 'Perth', 'Adelaide', 'Gold Coast', 'Hobart', 'Darwin', 'Canberra', 'Regional Australia'].map((city) => (
                  <span key={city} className="px-4 py-2 bg-[var(--blue-pale)] rounded-full text-sm font-medium" style={{ color: 'var(--navy-primary)' }}>
                    {city}
                  </span>
                ))}
              </div>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* Related Services */}
      <section className="section gray-bg">
        <div className="container mx-auto px-4">
          <FadeInUp>
            <div className="max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-6 text-center" style={{ color: 'var(--text-primary)' }}>
                Related Services
              </h2>
              <div className="grid md:grid-cols-3 gap-6">
                <Link to="/adhd-assessment" className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow group">
                  <h3 className="text-lg font-bold mb-2 flex items-center" style={{ color: 'var(--text-primary)' }}>
                    ADHD Assessment
                    <ArrowRight className="w-4 h-4 ml-2 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: 'var(--blue-primary)' }} />
                  </h3>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    Comprehensive ADHD assessments for adults and children via telehealth. Combined ASD and ADHD assessments are also available.
                  </p>
                </Link>
                <Link to="/therapy" className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow group">
                  <h3 className="text-lg font-bold mb-2 flex items-center" style={{ color: 'var(--text-primary)' }}>
                    Telehealth Therapy
                    <ArrowRight className="w-4 h-4 ml-2 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: 'var(--blue-primary)' }} />
                  </h3>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    Ongoing evidence-based therapy to support your wellbeing after diagnosis, including support for anxiety, burnout, and daily functioning.
                  </p>
                </Link>
                <Link to="/contact" className="bg-white p-6 rounded-xl shadow-md hover:shadow-lg transition-shadow group">
                  <h3 className="text-lg font-bold mb-2 flex items-center" style={{ color: 'var(--text-primary)' }}>
                    Get in Touch
                    <ArrowRight className="w-4 h-4 ml-2 opacity-0 group-hover:opacity-100 transition-opacity" style={{ color: 'var(--blue-primary)' }} />
                  </h3>
                  <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>
                    Have questions about whether an ASD assessment is right for you? Contact us to discuss your situation — no obligation.
                  </p>
                </Link>
              </div>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <FadeInUp>
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-center" style={{ color: 'var(--text-primary)' }}>
                Frequently Asked Questions About ASD Assessments
              </h2>
              <p className="text-lg mb-12 text-center" style={{ color: 'var(--text-secondary)' }}>
                Answers to the most common questions we receive about autism assessments for adults, children, women, and men.
              </p>
            </FadeInUp>

            <div className="space-y-4">
              {faqs.map((faq, index) => (
                <FadeInUp key={index} delay={index * 0.05}>
                  <div className="bg-white rounded-xl shadow-md overflow-hidden">
                    <button
                      onClick={() => setOpenFaq(openFaq === index ? null : index)}
                      data-testid={`faq-button-${index}`}
                      className="w-full px-6 py-4 text-left flex items-center justify-between hover:bg-[var(--gray-100)] transition-colors"
                    >
                      <span className="font-semibold text-lg" style={{ color: 'var(--text-primary)' }}>
                        {faq.question}
                      </span>
                      <ChevronDown
                        className={`transform transition-transform flex-shrink-0 ml-4 ${
                          openFaq === index ? 'rotate-180' : ''
                        }`}
                        style={{ color: 'var(--blue-primary)' }}
                      />
                    </button>
                    {openFaq === index && (
                      <div className="px-6 py-4 border-t" style={{ color: 'var(--text-secondary)' }}>
                        {faq.answer}
                      </div>
                    )}
                  </div>
                </FadeInUp>
              ))}
            </div>

            {/* FAQ Schema JSON-LD */}
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": faqs.map(faq => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": faq.answer
                }
              }))
            })}} />

            {/* Service Schema JSON-LD */}
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "MedicalProcedure",
              "name": "Autism Spectrum Disorder (ASD) Assessment",
              "description": "Comprehensive telehealth autism assessment for adults, children, women, and men across Australia. Includes clinical interview, standardised assessment tools, detailed diagnostic report, and feedback session.",
              "procedureType": "https://schema.org/DiagnosticProcedure",
              "howPerformed": "Conducted via secure telehealth video sessions",
              "preparation": "Pre-assessment questionnaires and developmental history forms",
              "followup": "Feedback session, comprehensive written report, and ongoing therapy options",
              "status": "https://schema.org/EventScheduled",
              "availableService": {
                "@type": "MedicalTherapy",
                "name": "Post-Assessment Telehealth Therapy"
              }
            })}} />

            {/* BreadcrumbList Schema */}
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "BreadcrumbList",
              "itemListElement": [
                { "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.navigatingminds.com.au/" },
                { "@type": "ListItem", "position": 2, "name": "Services", "item": "https://www.navigatingminds.com.au/services" },
                { "@type": "ListItem", "position": 3, "name": "ASD Assessment", "item": "https://www.navigatingminds.com.au/asd-assessment" }
              ]
            })}} />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section gray-bg">
        <div className="container mx-auto px-4">
          <FadeInUp>
            <div className="bg-[var(--blue-primary)] text-white rounded-3xl p-8 md:p-12 text-center max-w-4xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Ready to Start Your Autism Assessment?
              </h2>
              <p className="text-lg mb-4 opacity-90">
                Take the first step toward understanding yourself or your child better. Contact us to discuss your needs and book your assessment.
              </p>
              <p className="text-base mb-8 opacity-80">
                Medicare rebates and flexible payment plans available. No GP referral required to enquire.
              </p>
              <Link
                to="/contact"
                data-testid="asd-cta-button"
                className="px-8 py-4 bg-white text-[var(--blue-primary)] rounded-full font-medium hover:bg-[var(--gray-100)] transition-all duration-300 hover:scale-105 inline-block"
              >
                Book Your Assessment
              </Link>
            </div>
          </FadeInUp>
        </div>
      </section>

      {/* Sticky Mobile Contact Button */}
      <Link
        to="/contact#contact-form"
        className="md:hidden fixed bottom-6 right-6 z-50 bg-[#6bb8f2] hover:bg-[#5577E0] text-white px-6 py-4 rounded-full shadow-2xl flex items-center space-x-2 transition-all duration-300 hover:scale-105"
        data-testid="mobile-contact-button"
      >
        <Mail className="w-5 h-5" />
        <span className="font-semibold">Contact Us</span>
      </Link>
    </>
  );
};

export default ASDAssessmentPage;
